// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { route } from "@routes/helper";
import { connect } from "react-redux";
import { compose } from "redux";

import { PermissionEnum, ClientPermissionEnum } from "@modules/account/components";
import { USER_TYPES } from "@modules/account/constants";
import { View, Text } from "@components";
import { COMPLETE_SUPPORT } from "@modules/reporting/constants/reports";

import "./sidebar.style.scss";
import type { ProfileUser } from "@modules/account/types";
import { arrayRemoveItem } from "@utils/object";
import { getRouteParams, getClientIdParam } from "@selectors";
import { getEmployee } from "@modules/employee/selectors";
import type { EmployeeType } from "@modules/employee/types";

type PropsFromState = $Exact<{
  employee: EmployeeType,
}>;

type Props = {
  clientId: ?number,
  employeeId: ?number,
  user: ProfileUser,
  currentPath: string,
  permissions: { [appPermission: string]: boolean },
  clientSettings: { [appPermission: string]: boolean },
  navigate: (url: string) => void,
  isCollapsed: boolean,
  ...PropsFromLocalize,
  ...PropsFromState,
};

type NavNode = {
  id: string,
  name: string,
  url: string | null,
  icon: string,
  ellipsis?: boolean,
  appPermission: string | string[] | boolean,
  clientPermission?: string,
  children?: Array<NavNode>,
  isDisabled: boolean,
  isHidden?: boolean,
  color?: string,
  handleNavClick?: Function, // in case we want to provide custom behaviour, like for Empployees
  separator?: "top" | "right" | "bottom" | "left",
  isBeta?: boolean,
};

function Sidebar(props: Props) {
  const currentURL = props.currentPath;
  const isCollapsed = props.isCollapsed;
  const navTreeArray = generateNavTreeArray();
  const nodeByUrl = findNavNodeByProp(navTreeArray, "url", currentURL, true);

  const [state, setState] = useState({
    selectedNode: nodeByUrl || navTreeArray[0],
  });
  const [isExpanded, setIsExpanded] = useState([]);
  const selectedNode = state.selectedNode;

  function handleNavClick(nodeId: string, navigateNow?: boolean) {
    return function() {
      const node = findNavNodeByProp(navTreeArray, "id", nodeId);
      if (node && node.url) props.navigate(node.url);

      let res = [...isExpanded];
      if (res.includes(nodeId)) res = arrayRemoveItem(isExpanded, nodeId);
      else res = [...isExpanded, nodeId];

      setIsExpanded([...res]);
    };
  }

  function findNavNodeByProp(
    nodes: Array<Object>,
    prop: string,
    value: string,
    allowNotExactMatch: boolean = false,
  ) {
    for (const node of nodes) {
      const patternString = `${node[prop]}(?=\/[0-9a-z-]+)`;
      const regex = new RegExp(patternString, "g");
      const notExactMatch = (allowNotExactMatch && !!value.match(regex)) || false;
      if ((node[prop] && node[prop] === value) || notExactMatch) {
        return node;
      }
      if (node.children) {
        const childNode = findNavNodeByProp(node.children, prop, value, allowNotExactMatch);
        if (childNode) {
          return childNode;
        }
      }
    }
    return null;
  }

  function hasPermission(appPermission: string | string[] | boolean, clientPermission?: string) {
    if (typeof appPermission === "boolean") return appPermission;
    else {
      const { permissions, clientSettings } = props;
      let hasClientAccess;

      if (clientPermission && clientSettings) {
        hasClientAccess = !!clientSettings[clientPermission];
      } else hasClientAccess = !clientPermission;
      const hasPermission = !!Object.keys(permissions).filter((perm: string) => {
        if (typeof appPermission === "string") {
          return perm.indexOf(appPermission) > -1;
        } else {
          return typeof appPermission === "object" && appPermission.includes(perm);
        }
      }).length;

      return hasClientAccess && hasPermission;
    }
  }

  function renderMenuItem(el: NavNode, isChild?: boolean = false) {
    const children = el.children;
    const childWithSameUrl = children && children.find((child: NavNode) => child.url === el.url);

    const isActive = !childWithSameUrl && state.selectedNode?.id === el.id;
    const isDisabled = el.isDisabled;

    const url = el.url;

    const parentOfActiveChild =
      !isActive &&
      children &&
      children.find((child: NavNode) => {
        return state.selectedNode?.id === child.id;
      });

    const hasActiveChild =
      (isActive || parentOfActiveChild || isExpanded.includes(el.id)) && "expanded";
    return (
      <View
        name={el.id}
        className={["item-wrap", isActive && "active", isDisabled && "disabled"]}
        key={el.id}
        separator={el.hasOwnProperty("separator") && el.separator}
      >
        <View
          direction="row"
          className="item"
          overflow="hidden"
          alignItems="center"
          flex={1}
          onClick={
            !isActive && el.handleNavClick ? el.handleNavClick : handleNavClick(el.id, !!el.url)
          }
          onMouseOver={(e: React.MouseEvent) => {
            if (isCollapsed) {
              const label = e.target.querySelector(".label");
              if (label) {
                const rect = e.target.getBoundingClientRect();
                label.classList.add("act-as-tooltip");
                label.style.top = `${rect.top + 5}px`;
              }
            }
          }}
          onMouseOut={(e: React.MouseEvent) => {
            if (isCollapsed) {
              const label = e.target.querySelector(".label");
              if (label) {
                label.classList.remove("act-as-tooltip");
                label.style.top = "";
              }
            }
          }}
        >
          <View
            id={el.icon}
            color={el.color}
            className={[
              "no-pointer-events",
              "material-icons",
              isDisabled && "disabled",
              isChild && "child",
            ]}
          >
            {el.icon}
          </View>
          <View gap="1" direction="row" flex="1">
            <Text
              id={el.name}
              className={["label", el.ellipsis && "ellipsis"].join(" ").trim()}
              title={!!el.ellipsis ? el.name : ""}
              text={el.name}
            />
            {el.isBeta && (
              <span className="gs-badge background-color-black text-coffee w-50-px h-20-px br-5 fs-12">
                {props.t("BETA")}
              </span>
            )}
          </View>
          {children && (
            <View
              className={[
                "no-pointer-events",
                "material-icons",
                "right-arrow",
                hasActiveChild && "transform-rotate-90",
              ]}
            >
              keyboard_arrow_right
            </View>
          )}
        </View>
        {children && hasActiveChild && (
          <View className={["children-wrapper", hasActiveChild && "expanded"]}>
            {children && hasActiveChild && (
              <View>{children.map(child => renderMenuItem(child, true))}</View>
            )}
          </View>
        )}
      </View>
    );
  }

  function filterNavNodes(navTree: NavNode[]) {
    return navTree.filter((el: NavNode) => {
      if (el.children) el.children = filterNavNodes(el.children);
      return (
        el.isHidden !== true &&
        el.appPermission &&
        hasPermission(el.appPermission, el.clientPermission)
      );
    });
  }

  function renderNavigation() {
    const filteredItems = filterNavNodes(navTreeArray);

    const itemsToRender = filteredItems.map((el: NavNode, idx: number) => {
      return renderMenuItem(el);
    });
    return (
      <View name="render-navigation" flex={1} className="items-container">
        {itemsToRender}
      </View>
    );
  }

  function generateNavTreeArray(): Array<NavNode> {
    const clientId = props?.clientId;
    const { employeeId, clientSettings, user } = props;
    const healthProvidersAllowedUserTypes =
      [USER_TYPES.ADMIN, USER_TYPES.CLIENT_ADMIN, USER_TYPES.SERVICE_MANAGER].indexOf(
        user.userType,
      ) > -1;

    const showHealthReports =
      healthProvidersAllowedUserTypes && clientSettings?.serviceType === COMPLETE_SUPPORT;
    const showProvidersReports =
      healthProvidersAllowedUserTypes && clientSettings?.eapSignposting === true;
    const hideMyGoodShape = user.isNonService === true;

    return [
      //home
      {
        id: "home",
        name: props.t("HOME"),
        url: route("HOME_PATH"),
        icon: "home",
        appPermission: true,
        isDisabled: false,
        separator: "bottom",
      },
      //dashboard
      {
        id: "dashboard",
        name: props.t("DASHBOARD"),
        url: route("REPORTING_DASHBOARD_PATH", { clientId }),
        icon: "dashboard",
        appPermission: PermissionEnum.ACCESS_REPORTING_TOOLS,
        isDisabled: !clientId,
      },
      //tasks
      {
        id: "tasks",
        name: props.t("TASKS"),
        url: route("REPORTING_TOOLS_TASKS_PATH", { clientId }),
        icon: "checklist",
        appPermission: PermissionEnum.ACCESS_REPORTING_TOOLS,
        isDisabled: !clientId,
      },
      //absences
      {
        id: "absences",
        name: props.t("ABSENCES"),
        url: route("REPORTING_REPORTS_ABSENCE_DETAILS_PATH", { clientId }),
        icon: "sick",
        appPermission: PermissionEnum.ACCESS_REPORTING_REPORTS,
        isDisabled: !clientId,
      },
      //rtwInterviews
      {
        id: "rtwInterviews",
        name: props.t("RTW_INTERVIEWS"),
        url: route("REPORTING_REPORTS_RTW_DETAILS_PATH", { clientId }),
        icon: "pending_actions",
        appPermission: PermissionEnum.ACCESS_REPORTING_TOOLS,
        isDisabled: !clientId,
      },
      //alerts
      {
        id: "alerts",
        name: props.t("ALERTS"),
        url: route("REPORTING_REPORTS_ALERT_DETAILS_PATH", { clientId }),
        icon: "report_problem",
        appPermission: PermissionEnum.ACCESS_REPORTING_REPORTS,
        isDisabled: !clientId,
      },
      //employees
      {
        id: "employees",
        name: props.t("EMPLOYEES"),
        icon: "groups",
        url: null,
        appPermission: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
        isDisabled: !clientId,
        handleNavClick: function(e: EventHandler) {
          handleNavClick("employeeAudit", true)();
        },
        children: [
          {
            id: "employeeAudit",
            name: props.t("EMPLOYEE_SEARCH"),
            url: route("REPORTING_EMPLOYEES_SEARCH_PATH", { clientId }),
            icon: "person_search",
            appPermission: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
            isDisabled: !clientId, // Hiding it if we're not on the specific route....
          },

          {
            id: "employeesDetails",
            name: props?.employee?.firstName
              ? `${props?.employee?.firstName} ${props?.employee?.lastName}`
              : "",
            url: route("REPORTING_EMPLOYEE_DETAILS_PATH", {
              clientId,
              employeeId,
            }),
            icon: "manage_accounts",
            ellipsis: true,
            appPermission: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
            isDisabled: !clientId || !employeeId,
            isHidden: !props.employee,
          },
          // Hidden this page until final decision
          // {
          //   id: "currentForecasts",
          //   name: props.t("FORECASTS"),
          //   url: route("REPORTING_EMPLOYEE_FORECASTS_PATH", {
          //     clientId,
          //     employeeId,
          //   }),
          //   icon: "stacked_line_chart",
          //   clientPermission: ClientPermissionEnum.DATA_FORECASTING_ACCESS,
          //   appPermission: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
          //   isDisabled: !clientId || !employeeId,
          // },
        ],
      },

      //trends
      {
        id: "reports",
        name: props.t("TRENDS"),
        icon: "leaderboard",
        url: null,
        appPermission: [
          PermissionEnum.ACCESS_REPORTING_REPORTS,
          PermissionEnum.ACCESS_REPORTING_TRENDS,
          PermissionEnum.ACCESS_REPORTING_FOCUS_LIST,
          PermissionEnum.ACCESS_REPORTING_REPORTS,
          PermissionEnum.ACCESS_REPORTING_PRESENTATIONS,
          PermissionEnum.ACCESS_REPORTING_TOOLS,
        ],
        isDisabled: !clientId,

        children: [
          {
            id: "absenceRates",
            name: props.t("ABSENCE_RATES"),
            url: route("REPORTING_TRENDS_ABSENCE_RATE_PATH", { clientId }),
            icon: "trending_down",
            appPermission: PermissionEnum.ACCESS_REPORTING_TRENDS,
            isDisabled: !clientId,
          },
          {
            id: "absenceReasons",
            name: props.t("ABSENCE_REASONS"),
            url: route("REPORTING_TRENDS_ABSENCE_REASON_PATH", { clientId }),
            icon: "contact_support",
            appPermission: PermissionEnum.ACCESS_REPORTING_TRENDS,
            isDisabled: !clientId,
          },
          {
            id: "complianceTrends",
            name: props.t("COMPLIANCE_TRENDS"),
            url: route("REPORTING_TRENDS_COMPLIANCE_PATH", { clientId }),
            icon: "trending_up",
            appPermission: PermissionEnum.ACCESS_REPORTING_TRENDS,
            isDisabled: !clientId,
          },

          {
            id: "benchmarks",
            name: props.t("BENCHMARKING_INDEX"),
            url: route("REPORTING_TOOLS_INDEX_PATH", { clientId }),
            icon: "travel_explore",
            appPermission: PermissionEnum.ACCESS_REPORTING_TOOLS,
            isDisabled: !clientId,
          },
          {
            id: "presentations",
            name: props.t("PRESENTATION_BUILDER"),
            url: route("REPORTING_MI_PRESENTATIONS_PATH", { clientId }),
            icon: "assessment",
            appPermission: PermissionEnum.ACCESS_REPORTING_PRESENTATIONS,
            isDisabled: !clientId,
          },
          {
            id: "covid19",
            name: props.t("COVID_19_TRACKER"),
            url: route("REPORTING_TOOLS_CORONAVIRUS_PATH", { clientId }),
            icon: "coronavirus",
            appPermission: PermissionEnum.ACCESS_REPORTING_TOOLS,
            isDisabled: !clientId,
          },
        ],
      },
      //focusList
      {
        id: "trends",
        name: props.t("FOCUS_LISTS"),
        icon: "format_list_numbered_rtl",
        url: null,
        appPermission: [
          PermissionEnum.ACCESS_REPORTING_REPORTS,
          PermissionEnum.ACCESS_REPORTING_FOCUS_LIST,
          PermissionEnum.ACCESS_REPORTING_REPORTS,
          PermissionEnum.ACCESS_REPORTING_PRESENTATIONS,
          PermissionEnum.ACCESS_REPORTING_TOOLS,
        ],
        isDisabled: !clientId,
        separator: "bottom",
        children: [
          {
            id: "employeeFocusList",
            name: props.t("EMPLOYEE_FOCUS_LIST"),
            url: route("REPORTING_FOCUS_LISTS_EMPLOYEE_PATH", { clientId }),
            icon: "person",
            appPermission: PermissionEnum.ACCESS_REPORTING_FOCUS_LIST,
            isDisabled: !clientId,
          },
          {
            id: "departmentFocusList",
            name: props.t("DEPARTMENT_FOCUS_LIST"),
            url: route("REPORTING_FOCUS_LISTS_DEPARTMENT_PATH", { clientId }),
            icon: "factory",
            appPermission: PermissionEnum.ACCESS_REPORTING_FOCUS_LIST,
            isDisabled: !clientId,
          },
          {
            id: "managerFocusList",
            name: props.t("MANAGER_FOCUS_LIST"),
            url: route("REPORTING_FOCUS_LISTS_MANAGER_PATH", { clientId }),
            icon: "badge",
            appPermission: PermissionEnum.ACCESS_REPORTING_FOCUS_LIST,
            isDisabled: !clientId,
          },
        ],
      },
      //dataForecasts
      {
        id: "dataForecasts",
        name: props.t("FORECAST"),
        url: route("REPORTING_DATA_FORECASTS_PATH", { clientId }),
        icon: "tips_and_updates",
        appPermission: PermissionEnum.APP_ACCESS_REPORTING_DATA_FORECASTING,
        clientPermission: ClientPermissionEnum.DATA_FORECASTING_ACCESS,
        isDisabled: !clientId,
        color: "mint-d1",
        isBeta: true,
      },
      //healthReports
      {
        id: "healthReports",
        name: props.t("HEALTH"),
        icon: "health_and_safety",
        url: null,
        color: "ice-d2",
        appPermission: PermissionEnum.ACCESS_REPORTING_HEALTH,
        isDisabled: !clientId,
        isHidden: !showHealthReports,
        children: [
          {
            id: "workforce_health",
            name: props.t("WORKFORCE_HEALTH"),
            url: route("REPORTING_WORKFORCE_HEALTH_PATH", { clientId }),
            icon: "monitor_heart",
            appPermission: PermissionEnum.ACCESS_REPORTING_HEALTH,
            isDisabled: !clientId,
          },
          {
            id: "care_plans",
            name: props.t("CARE_PLANS"),
            url: route("REPORTING_CARE_PLANS_PATH", { clientId }),
            icon: "run_circle",
            appPermission: PermissionEnum.ACCESS_REPORTING_HEALTH,
            isDisabled: !clientId,
          },
          {
            id: "wellbeing_advice",
            name: props.t("WELLBEING_ADVICE_TITLE"),
            url: route("REPORTING_WELLBEING_ADVICE_PATH", { clientId }),
            icon: "medical_information",
            appPermission: PermissionEnum.ACCESS_REPORTING_HEALTH,
            isDisabled: !clientId,
          },
          {
            id: "nurse_assessments",
            name: props.t("NURSE_ASSESSMENTS"),
            url: route("REPORTING_NURSE_ASSESSMENTS_PATH", { clientId }),
            icon: "support_agent",
            appPermission: PermissionEnum.ACCESS_REPORTING_HEALTH,
            isDisabled: !clientId,
          },
        ],
      },
      //monitoring
      {
        id: "monitor",
        name: props.t("MONITOR"),
        color: "risk-very-high",
        url: null,
        icon: "query_stats",
        clientPermission: ClientPermissionEnum.ABSENCE_MONITORING,
        appPermission: PermissionEnum.ACCESS_REPORTING_REPORTS,
        isDisabled: !clientId,
        children: [
          {
            id: "employee_status",
            name: props.t("EMPLOYEE_STATUS"),
            url: route("REPORTING_REPORTS_ABSENCE_MONITORING_PATH", { clientId }),
            icon: "assignment_ind",
            appPermission: PermissionEnum.ACCESS_REPORTING_REPORTS,
            isDisabled: !clientId,
          },
          {
            id: "kpi_report",
            name: props.t("KPI_COMPLIANCE"),
            url: route("REPORTS_KPI_COMPLIANCE_PATH", { clientId }),
            icon: "pie_chart",
            appPermission: PermissionEnum.PRIVILEGE_REPORTS_KPI_COMPLIANCE,
            isDisabled: !clientId,
          },
        ],
      },
      //navigation
      {
        id: "navigation",
        name: props.t("NAVIGATOR"),
        icon: "recent_actors",
        color: "berry-d1",
        url: null,
        clientPermission: ClientPermissionEnum.REFERRAL_TRACKING,
        appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
        isDisabled: !clientId,
        children: [
          {
            id: "new-referrals",
            name: props.t("NEW_REFERRALS"),
            url: route("REPORTING_NEW_REFERRALS", { clientId }),
            icon: "markunread_mailbox",
            appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
            isDisabled: !clientId,
          },
          {
            id: "active-referrals",
            name: props.t("ACTIVE_REFERRALS"),
            url: route("REPORTING_ACTIVE_REFERRALS", { clientId }),
            icon: "folder_shared",
            appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
            isDisabled: !clientId,
          },
          {
            id: "closed-referrals",
            name: props.t("CLOSED_REFERRALS"),
            url: route("REPORTING_CLOSED_REFERRALS", { clientId }),
            icon: "fact_check",
            appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
            isDisabled: !clientId,
          },
          {
            id: "manager-kpi",
            name: props.t("MANAGER_KPIS"),
            url: route("REPORTING_MANAGER_KPI", { clientId }),
            icon: "assessment",
            appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
            isDisabled: !clientId,
          },
          {
            id: "outcomes-report",
            name: props.t("OUTCOMES"),
            url: route("REPORTING_OUTCOMES_REPORT", { clientId }),
            icon: "table_chart",
            appPermission: PermissionEnum.ACCESS_REPORTING_REFERRALS,
            isDisabled: !clientId,
          },
        ],
      },
      //providersReports
      {
        id: "providersReports",
        name: props.t("PROVIDER"),
        icon: "store",
        url: route("REPORTING_PROVIDERS_REPORT_PATH", { clientId }),
        appPermission: PermissionEnum.ACCESS_REPORTING_PROVIDERS,
        isDisabled: !clientId,
        isHidden: !showProvidersReports,
        color: "pumpkin-orange",
        separator: "bottom",
      },

      //myGoodshape
      {
        id: "myGoodshape",
        name: props.t("MY_GOODSHAPE"),
        icon: "medical_information",
        url: null,
        appPermission: PermissionEnum.ACCESS_MY_GOODSHAPE,
        isDisabled: false,
        isHidden: hideMyGoodShape,
        children: [
          {
            id: "my_absence",
            name: props.t("MY_ABSENCES"),
            url: route("MY_ABSENCES_PATH", { clientId }),
            icon: "personal_injury",
            appPermission: PermissionEnum.ACCESS_MY_GOODSHAPE,
            isDisabled: false,
          },
          {
            id: "advice_library",
            name: props.t("ADVICE_LIBRARY"),
            url: route("ADVICE_LIBRARY_PATH", { clientId }),
            icon: "menu_book",
            appPermission: PermissionEnum.ACCESS_MY_GOODSHAPE,
            isDisabled: false,
          },
          {
            id: "service_directory",
            name: props.t("SERVICE_DIRECTORY"),
            url: route("SERVICE_DIRECTORY_PATH", { clientId }),
            icon: "store",
            appPermission: PermissionEnum.ACCESS_MY_GOODSHAPE,
            isDisabled: false,
          },
        ],
      },
      //adminConsole
      {
        id: "adminConsole",
        name: props.t("ADMIN_CONSOLE"),
        icon: "settings",
        url: null,
        appPermission: PermissionEnum.ACCESS_ADMIN,
        isDisabled: !clientId,
        children: [
          {
            id: "acccount",
            name: props.t("ACCOUNT"),
            url: route("ADMINISTRATION_CLIENT_DETAILS_PATH", { clientId }),
            icon: "settings_suggest",
            appPermission: PermissionEnum.ACCESS_ADMIN_CLIENT,
            isDisabled: !clientId,
          },
          {
            id: "auditLogs",
            name: props.t("AUDIT_LOGS"),
            url: route("ADMINISTRATION_CLIENT_AUDIT_LOGS_PATH", { clientId }),
            icon: "history",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
          {
            id: "dataPage",
            name: props.t("DATA"),
            url: route("ADMINISTRATION_DATA_PAGE_PATH", { clientId }),
            icon: "storage",
            appPermission: PermissionEnum.ACCESS_ADMIN_DATA,
            isDisabled: !clientId,
          },
          {
            id: "absenceEvents",
            name: props.t("FEATURES"),
            url: route("ADMINISTRATION_CLIENT_FEATURES_PATH", { clientId }),
            icon: "tour",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
          {
            id: "hierarchyTree",
            name: props.t("HIERARCHY_TREE"),
            url: route("ADMINISTRATION_HIERARCHY_CHANGES_PATH", { clientId }),
            icon: "lan",
            appPermission: PermissionEnum.ACCESS_ADMIN_HIERARCHY,
            isDisabled: !clientId,
          },
          {
            id: "notifications",
            name: props.t("NOTIFICATIONS"),
            url: route("ADMINISTRATION_SERVICE_NOTIFICATIONS_PAGE", { clientId }),
            icon: "notifications_active",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
          {
            id: "security",
            name: props.t("SECURITY"),
            url: route("ADMINISTRATION_SERVICE_SECURITY", { clientId }),
            icon: "key",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
          {
            id: "services",
            name: props.t("SERVICES"),
            url: route("ADMINISTRATION_SERVICE_WELLBEING_SERVICES", { clientId }),
            icon: "double_arrow",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
          {
            id: "settings",
            name: props.t("SETTINGS"),
            url: route("ADMINISTRATION_SERVICE_SETTINGS", { clientId }),
            icon: "display_settings",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },

          {
            id: "users",
            name: props.t("USERS"),
            url: route("ADMINISTRATION_SERVICE_USERS", { clientId }),
            icon: "lock_person",
            appPermission: PermissionEnum.ACCESS_ADMIN_SERVICE,
            isDisabled: !clientId,
          },
        ],
      },

      //
    ];
  }

  const navigation = renderNavigation();
  return (
    <View flex={1} position="sticky" className={["sidebar", props.isCollapsed && "collapsed"]}>
      <View
        name="logo-container"
        direction="row"
        className="logo"
        justifyContent="center"
        alignItems="center"
        gap={isCollapsed ? 0 : 2}
        height={`67px`}
        separator="bottom"
      >
        <a href={route("HOME_PATH")} className="picto"></a>
        <Text className="text" text={props.t("CENTRAL")} />
      </View>
      {navigation}
    </View>
  );
}
const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    employee: getEmployee(parseInt(ownProps.employeeId))(state),
  };
};
export default compose(localize(), connect(mapStateToProps, null))(Sidebar);
